/**
 * ScreenHeader — felles topp for skjermene i skallet (#153, PLAN.md steg 3).
 *
 * Skissene har samme topp overalt: en valgfri tilbakeknapp, sidetittelen, og
 * til høyre avataren som åpner Meg (`/settings`). Meg er ikke lenger en fane i
 * hovedmenyen, så avataren er inngangen dit.
 *
 * I steg 3 bruker bare skallet denne (InteractiveMode, uten tittel); skjermene
 * tar den i bruk med tittel i sine egne steg. Hjem (#154) har sin egen topp
 * med hilsen og streak, og bruker bare `MeAvatarLink`.
 *
 * `trailing` står foran avataren, for en knapp eller et merke en skjerm vil ha
 * i toppen.
 *
 * #187: knappen «Send tilbakemelding» står mellom `trailing` og avataren, så
 * skjermbildet viser skjermen brukeren står på. Den følger avataren: uten
 * avatar (Meg) er det raden i Meg som gjelder.
 */
import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Icon } from './primitives/Icon';
import { InitialAvatar, asColorSlot } from './primitives/InitialAvatar';
import { GLASS_SURFACE } from './primitives/Card';
import { useSession } from '../features/auth/useSession';
import { FeedbackWidget } from '../features/feedback/FeedbackWidget';

/** Avataren til den innloggede, som lenke til Meg. 44 px treffflate. */
export function MeAvatarLink(): ReactNode {
  const { t } = useTranslation();
  const { data: session } = useSession();
  const member = session?.member;
  return (
    <Link
      to="/settings"
      data-testid="nav-tab-me"
      aria-label={t('nav.me')}
      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full"
    >
      {member ? (
        <InitialAvatar
          name={member.short_name}
          colorSlot={asColorSlot(member.color_token)}
          size={40}
          decorative
        />
      ) : (
        // Kjøkkenskjermen kan stå uten økt (DASH-06); Meg sender da til /login.
        <span
          aria-hidden
          className={`${GLASS_SURFACE} flex h-10 w-10 items-center justify-center rounded-full text-ink`}
        >
          <Icon.User size={20} />
        </span>
      )}
    </Link>
  );
}

interface ScreenHeaderProps {
  title?: ReactNode;
  /**
   * Tilbakeknapp til venstre for tittelen. `testId` erstatter
   * `screen-header-back`. Med `history` går knappen ett steg tilbake når det
   * finnes et steg i appen å gå til, og ellers til `to` (Meg nås fra
   * avataren på alle skjermene, #159 steg 9d).
   */
  back?: { to: string; label?: string; testId?: string; history?: boolean };
  trailing?: ReactNode;
  /** Uten avataren — på Meg, som avataren selv åpner (#159, steg 9d). */
  hideAvatar?: boolean;
  /** Uten tilbakemeldingsknappen — skjermen har den et annet sted (#187). */
  hideFeedback?: boolean;
  className?: string;
  'data-testid'?: string;
}

export function ScreenHeader({
  title,
  back,
  trailing,
  hideAvatar = false,
  hideFeedback = false,
  className,
  'data-testid': testId = 'screen-header',
}: ScreenHeaderProps): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <header
      data-testid={testId}
      className={['flex items-center gap-3', className].filter(Boolean).join(' ')}
    >
      {back ? (
        <Link
          to={back.to}
          onClick={(e) => {
            // React Router legger posisjonen i historikken i `history.state.idx`.
            const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
            if (back.history && idx > 0) {
              e.preventDefault();
              void navigate(-1);
            }
          }}
          aria-label={back.label ?? t('common.back')}
          data-testid={back.testId ?? 'screen-header-back'}
          className={`${GLASS_SURFACE} flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-ink`}
        >
          <Icon.ChevL size={20} />
        </Link>
      ) : null}
      {title !== undefined ? (
        <h1 className="min-w-0 flex-1 truncate text-heading-l font-extrabold tracking-tight text-ink">
          {title}
        </h1>
      ) : (
        <span className="flex-1" />
      )}
      {trailing}
      {hideAvatar ? null : (
        <>
          {hideFeedback ? null : <FeedbackWidget variant="icon" />}
          <MeAvatarLink />
        </>
      )}
    </header>
  );
}
